/**
 * Admin advertising campaign management + public corner ad for mobile apps.
 */
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function adminAuthHeaders() {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token}` };
}

export interface AdvertisingCampaign {
  id: number;
  campaign_id: string;
  title: string;
  ad_type: string;
  status: string;
  duration_days: number;
  image_url?: string | null;
  link_url?: string | null;
  starts_at: string;
  expires_at: string;
  created_at: string;
  total_views?: number;
  total_clicks?: number;
  remaining_days?: number;
}

export interface CornerAd {
  campaign_id: string;
  title: string;
  image_url: string;
  link_url?: string | null;
}

class AdvertisingService {
  async getAdminCampaigns(page = 1, pageSize = 50, status?: string) {
    const safePage = Math.max(1, parseInt(String(page), 10) || 1);
    const safePageSize = Math.min(200, Math.max(1, parseInt(String(pageSize), 10) || 50));
    const params: Record<string, string | number> = {
      skip: (safePage - 1) * safePageSize,
      limit: safePageSize,
    };
    if (status) params.status = status;
    const response = await axios.get(`${API_BASE_URL}/advertising/admin/campaigns`, {
      headers: adminAuthHeaders(),
      params,
    });
    return response.data;
  }

  async uploadImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
    const response = await axios.post(`${API_BASE_URL}/advertising/admin/upload-image`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.image_url || response.data?.url || '';
  }

  async createCornerCampaign(payload: {
    title: string;
    image_url: string;
    link_url?: string;
    duration_days: number;
  }) {
    const response = await axios.post(`${API_BASE_URL}/advertising/admin/campaigns`, payload, {
      headers: adminAuthHeaders(),
    });
    return response.data;
  }

  async getActiveCornerAd(): Promise<CornerAd | null> {
    const response = await axios.get(`${API_BASE_URL}/advertising/corner`);
    return response.data?.ad || null;
  }

  async recordCornerClick(campaignId: string) {
    await axios.post(`${API_BASE_URL}/advertising/corner/${campaignId}/click`);
  }

  async adminDeleteCampaign(campaignId: number | string) {
    const response = await axios.delete(
      `${API_BASE_URL}/advertising/admin/campaigns/${campaignId}`,
      { headers: adminAuthHeaders() }
    );
    return response.data;
  }

  async adminPauseCampaign(campaignId: string) {
    const response = await axios.post(
      `${API_BASE_URL}/advertising/admin/campaigns/${campaignId}/pause`,
      {},
      { headers: adminAuthHeaders() }
    );
    return response.data;
  }

  async adminResumeCampaign(campaignId: string) {
    const response = await axios.post(
      `${API_BASE_URL}/advertising/admin/campaigns/${campaignId}/resume`,
      {},
      { headers: adminAuthHeaders() }
    );
    return response.data;
  }

  async adminStopCampaign(campaignId: string) {
    const response = await axios.post(
      `${API_BASE_URL}/advertising/admin/campaigns/${campaignId}/stop`,
      {},
      { headers: adminAuthHeaders() }
    );
    return response.data;
  }
}

export default new AdvertisingService();
